import React, { useEffect, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import { generateEntityReference } from '../../shared/utils/generateEntityReference';
import { useSettings } from '../../core/contexts/SettingsContext';
import {
  getVentesEntityPrefix,
  isVentesAutoReferenceEnabled,
  resolveCustomerSegmentLabel,
  normalizeCustomerSegmentCode,
} from './ventesConfigDefaults';
import {
  sliceQawaemStats,
  QAWAEM_KPI_GRID_CLASS,
} from '../../products/saudi/components/QawaemErpListPageShell';
import { isDateInRange } from '../../products/saudi/utils/dateRangePresets';

export { generateEntityReference, resolveCustomerSegmentLabel, normalizeCustomerSegmentCode };

/** Ventes entity reference field + prefix mapping (settings.configuration.entityPrefixes). */
export const VENTES_ENTITY_REF_DEFAULTS = {
  priceRule: { prefixKey: 'priceRule', fallback: 'PRM', field: 'rule_reference' },
  salesOrder: { prefixKey: 'salesOrder', fallback: 'CMD', field: 'order_number' },
  sale: { prefixKey: 'sale', fallback: 'VENT', field: 'sale_number' },
  delivery: { prefixKey: 'delivery', fallback: 'LIV', field: 'delivery_reference' },
  salesReturn: { prefixKey: 'salesReturn', fallback: 'SR', field: 'return_number' },
  commission: { prefixKey: 'commission', fallback: 'COM', field: 'commission_reference' },
};

/** Next ventes entity رمز (PRM-001, CMD-002, …). */
export function generateVentesEntityReference(settings, entityKey, records) {
  const def = VENTES_ENTITY_REF_DEFAULTS[entityKey];
  if (!def) return '';
  const prefix = getVentesEntityPrefix(settings, entityKey, def.fallback);
  return generateEntityReference(prefix, records, (r) => r[def.field]);
}

/** Use trimmed modal value or auto-generate via entityPrefixes. */
export function resolveVentesEntityReference(settings, entityKey, records, currentValue) {
  const trimmed = String(currentValue ?? '').trim();
  if (trimmed) return trimmed;
  return generateVentesEntityReference(settings, entityKey, records);
}

/** Stable callback to generate the next reference for a ventes entity list. */
export function useVentesEntityReference(entityKey, records) {
  const { settings } = useSettings();
  return useCallback(
    () => generateVentesEntityReference(settings, entityKey, records),
    [settings, entityKey, records],
  );
}

/** Shared editable رمز input classes for ventes CRUD modals. */
export const VENTES_REF_INPUT_CLASS =
  'w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono';

export { sliceQawaemStats, QAWAEM_KPI_GRID_CLASS };

/** Lock body scroll while create/view CRUD modals are open. */
export function useCrudModalBodyLock(...openFlags) {
  useEffect(() => {
    if (!openFlags.some(Boolean)) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, openFlags);
}

/** Cap ventes KPI/stat card entries to max 4 (Qawaem brand). */
export function mapVentesStatCards(entries = [], max = 4) {
  return sliceQawaemStats(entries, max).map((stat, i) => ({
    key: stat.key || stat.label || stat.title || i,
    label: stat.label || stat.title,
    value: stat.value,
    icon: stat.icon,
    variant: stat.variant || (i % 2 === 0 ? 'copper' : 'forest'),
    className: stat.className,
  }));
}

/** Bulk-delete button for Qawaem title-row filterActions (Products-style). */
export function buildVentesBulkDeleteFilterAction({ selectedCount, onDelete, label }) {
  if (!selectedCount) return null;
  return (
    <button
      type="button"
      onClick={onDelete}
      className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
    >
      <Trash2 size={16} />
      <span>{label}</span>
    </button>
  );
}

/** Indeterminate select-all helpers for ventes list tables. */
export function computeVentesRowSelection(filteredIds, selectedIds) {
  const allRowsSelected = filteredIds.length > 0 && filteredIds.every((id) => selectedIds.includes(id));
  const someRowsSelected = selectedIds.length > 0 && !allRowsSelected;
  return { allRowsSelected, someRowsSelected };
}

const ventesImportExportNoop = () => {};

/** Import/export toolbar props for ventes list pages. */
export function buildVentesImportExportProps({
  showImportDropdown,
  setShowImportDropdown,
  showExportDropdown,
  setShowExportDropdown,
  importDropdownRef,
  exportDropdownRef,
  p,
  t,
  isGeneratingPDF = false,
  handlers = {},
}) {
  return {
    importOpen: showImportDropdown,
    exportOpen: showExportDropdown,
    onToggleImport: () => {
      setShowImportDropdown(!showImportDropdown);
      setShowExportDropdown(false);
    },
    onToggleExport: () => {
      setShowExportDropdown(!showExportDropdown);
      setShowImportDropdown(false);
    },
    importRef: importDropdownRef,
    exportRef: exportDropdownRef,
    importLabel: p('import', { defaultValue: t('common.import', { defaultValue: 'استيراد' }) }),
    exportLabel: t('common.export', { defaultValue: 'تصدير' }),
    generatingLabel: p('generating', { defaultValue: t('common.loading', { defaultValue: 'جاري الإنشاء...' }) }),
    isGeneratingPDF,
    onImportCSV: handlers.onImportCSV || handlers.onImport || ventesImportExportNoop,
    onImportExcel: handlers.onImportExcel || handlers.onImport || ventesImportExportNoop,
    onImportJSON: handlers.onImportJSON || ventesImportExportNoop,
    onExportCSV: handlers.onExportCSV || handlers.onExport || ventesImportExportNoop,
    onExportExcel: handlers.onExportExcel || handlers.onExport || ventesImportExportNoop,
    onExportJSON: handlers.onExportJSON || ventesImportExportNoop,
    onExportPDF: handlers.onExportPDF || ventesImportExportNoop,
    showJsonImportExport: handlers.showJsonImportExport ?? false,
  };
}

/** Build column picker defs from { key, label } entries. */
export function buildVentesColumnDefs(columns) {
  return columns.map((col) => ({
    key: col.key,
    label: col.label,
    checked: col.checked !== false,
    required: col.required,
  }));
}

/** Initial visibility map for table data-column keys. */
export function initVentesColumnVisibility(keys) {
  return Object.fromEntries(keys.map((key) => [key, true]));
}

/** Qawaem column-picker defs from { key, label, required? } + visibility state. */
export function buildVentesQawaemColumnDefs(columns, columnVisibility) {
  return columns.map((col) => ({
    key: col.key,
    label: col.label,
    checked: columnVisibility[col.key] !== false,
    required: col.required,
  }));
}

export function isVentesColumnVisible(columnVisibility, key) {
  return columnVisibility[key] !== false;
}

/** Header date-filter preset range (SaudiHeader) — no-op when range is empty. */
export function matchesVentesListDateRange(dateValue, dateRange) {
  return isDateInRange(dateValue, dateRange);
}

/** Cap list rows to configured itemsPerPage. */
export function sliceVentesListItems(items = [], itemsPerPage = 50) {
  const limit = Math.max(1, parseInt(itemsPerPage, 10) || 50);
  return items.slice(0, limit);
}

/** Shown when a ventes submodule is turned off in إعدادات المبيعات. */
export function VentesConfigNotice({ message }) {
  if (!message) return null;
  return (
    <div className="mb-4 rounded-2xl border border-amber-500/35 bg-amber-500/12 px-4 py-3 text-amber-100 text-sm text-right leading-relaxed">
      {message}
    </div>
  );
}
